import React, { useState } from 'react';
import {
  IconMail,
  IconX,
  IconLoader2,
  IconAlertCircle,
  IconKey,
  IconArrowLeft,
  IconCircleCheck
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';

const AddEmailBanner = () => {
  const { updateUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = ввод email, 2 = ввод кода
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const closeModal = () => {
    setOpen(false);
    setStep(1);
    setEmail('');
    setCode('');
    setError(null);
    setSuccess(false);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Введите корректный email');
      return;
    }
    setLoading(true);
    try {
      await authAPI.sendEmailCode(email.trim());
      setStep(2);
    } catch (err) {
      setError(err.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    if (!code.trim()) {
      setError('Введите код из письма');
      return;
    }
    setLoading(true);
    try {
      const data = await authAPI.verifyEmailCode(code.trim());
      updateUser({ email: data.email || email.trim(), email_verified: true });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Неверный код');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError(null);
    try {
      await authAPI.sendEmailCode(email.trim());
      setError('Код отправлен повторно');
    } catch (err) {
      setError(err.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Alert Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl mb-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <IconMail className="w-5 h-5 text-yellow-500" />
          </div>
          <div>
            <p className="text-yellow-700 dark:text-yellow-300 font-medium text-sm">
              Привяжите email к аккаунту
            </p>
            <p className="text-yellow-600/80 dark:text-yellow-400/70 text-xs">
              Это позволит восстановить доступ и сбрасывать пароль по почте
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg font-medium text-black text-sm transition-colors flex-shrink-0"
        >
          Добавить почту
        </button>
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeModal} />

          <div className="relative bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-800 rounded-2xl p-5 sm:p-8 max-w-md w-full mx-4 animate-fade-in">
            <button
              onClick={closeModal}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-900 dark:text-dark-500 dark:hover:text-white transition-colors"
            >
              <IconX className="w-5 h-5" />
            </button>

            {success ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconCircleCheck className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Email привязан
                  </h3>
                  <p className="text-gray-600 dark:text-dark-400 text-sm">
                    Теперь вы можете сбрасывать пароль по почте
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white transition-colors"
                >
                  Готово
                </button>
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    {step === 1 ? (
                      <IconMail className="w-8 h-8 text-primary-500" />
                    ) : (
                      <IconKey className="w-8 h-8 text-primary-500" />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {step === 1 ? 'Привязка email' : 'Код из письма'}
                  </h3>
                  <p className="text-gray-600 dark:text-dark-400 text-sm">
                    {step === 1
                      ? 'Укажите email, к которому будет привязан аккаунт. Мы отправим на него код подтверждения.'
                      : `Мы отправили код на ${email}. Введите его ниже.`}
                  </p>
                </div>

                {error && (
                  <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
                    error === 'Код отправлен повторно'
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <IconAlertCircle className={`w-5 h-5 flex-shrink-0 ${
                      error === 'Код отправлен повторно' ? 'text-blue-400' : 'text-red-400'
                    }`} />
                    <p className={`text-sm ${error === 'Код отправлен повторно' ? 'text-blue-400' : 'text-red-400'}`}>{error}</p>
                  </div>
                )}

                {step === 1 ? (
                  <form onSubmit={handleSendCode} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <IconLoader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <span>Отправить код</span>
                      )}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyCode} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">
                        Код подтверждения
                      </label>
                      <div className="relative">
                        <IconKey className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-600" />
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="••••••"
                          inputMode="numeric"
                          className="w-full pl-10 pr-4 py-3 bg-white dark:bg-dark-950 border border-gray-300 dark:border-dark-800 rounded-lg text-center tracking-[0.5em] text-lg font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-600 focus:outline-none focus:border-primary-500 transition-colors"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <IconLoader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <span>Подтвердить</span>
                      )}
                    </button>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => { setStep(1); setError(null); }}
                        className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
                      >
                        <IconArrowLeft className="w-4 h-4" />
                        Изменить email
                      </button>
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={loading}
                        className="text-sm text-primary-500 hover:underline disabled:opacity-50"
                      >
                        Отправить ещё раз
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AddEmailBanner;
